// Dispatching one queued batch over `firebase/firestore/lite` — #410, #207's U8.
//
// `writes.js` calls in here when `liteWriteFlag.js` says so, and everything
// that makes lite different from the full SDK lives in this file: where the
// handle comes from, which sentinel instances a lite batch will accept, and
// what a rejection means.
//
// ── Why lite is a second Firestore rather than a replacement ───────────────
//
// `firebase/firestore/lite` registers itself on the `FirebaseApp` under its own
// component name, so `getFirestore(app)` out of lite and `getFirestore(app)`
// out of the full SDK hand back **different objects of different classes**;
// `test/domain/liteWritePath.test.js` asserts that each refuses the other's
// (`invalid-argument`: "Did you pass a reference from a different Firestore
// SDK?"). So lite **adds** a Firestore; it removes none. Reads stay where they
// are — lite exports no `onSnapshot`, `persistentLocalCache`,
// `waitForPendingWrites`, `getDocsFromServer` or `clearIndexedDbPersistence`.
//
// The import below is dynamic and Rollup gives it its own chunk, so the lite
// SDK is fetched only by a session that dispatches over lite.
//
// `persistentLocalCache` stays and becomes a **read** cache only — there is no
// option to have one without the other. `Persistence` in the full SDK exposes
// `getMutationQueue(user, indexManager)` as a method, and both implementations
// (`IndexedDbPersistence`, `MemoryPersistence`) construct one on demand; no
// setting disables it. So lite dispatch **starves** the SDK's queue rather
// than switching it off: its IndexedDB stores stay empty, because nothing calls
// `setDoc`/`writeBatch` on the full SDK's handle.
//
// ── What still depends on the SDK's mutation queue, by binding ─────────────
//
// Three bindings, the first two in `src/`:
//
//   1. **`d.metadata.hasPendingWrites`**, at `useCollectionWindow.js` — the
//      *snapshot* read arm's `pendingWrite`. This is the blocker #410 records,
//      and `liteWriteFlag.js` makes the pair unreachable.
//      `useDocumentWindow.js`'s snapshot arm hardcodes `pendingWrite: false`,
//      so it binds nothing here.
//   2. **`waitForPendingWrites(db)`**, one call, at `SyncContext.jsx` — #167's
//      gate. Under lite dispatch it resolves immediately and always: vacuous,
//      not unsafe, as the call site explains.
//   3. **Latency compensation**, which is not a symbol at all: `d.data()` on
//      the snapshot arm is the server's document with the local mutation
//      applied over it. Same binding as 1 and the same answer — it is what
//      makes a teacher's own save appear (#237), and it is why that arm cannot
//      be served by lite.
//
// **And two properties of the SDK's queue that are not bindings (#423):**
//
//   4. **Order across documents.** The SDK sends every batch on one write
//      stream in the order it was handed over; our queue's per-document FIFO
//      leaned on that. Lite has no stream, so the queue takes the core's
//      studio-order rule while lite dispatches — `inStudioOrder` in
//      `queue.js`, `ready_batches_in_studio_order` in `core/src/queue.rs`.
//   5. **Waking a sibling tab.** `persistentMultipleTabManager` shares the
//      SDK's mutation queue across tabs, so another tab's listeners fired for
//      a write made in this one. `writes.js` posts queue transitions on a
//      `BroadcastChannel` while lite dispatches — `tellSiblingTabs`.
//
// Everything else that reads "the SDK is holding something" is a *read* API,
// not the mutation queue — `getDocsFromServer` (`useStudents.jsx`,
// `useFamilies.jsx`, `queuedHistory.js`, `serverConfirmed.js`) and
// `clearIndexedDbPersistence` (`AuthContext.jsx`, `accountDeletion.js`) — and
// none of them moves.
//
// Lite cannot replace the full SDK: it has no listeners, and every read in the
// app is an `onSnapshot` in `useCollectionWindow.js` or `useDocumentWindow.js`.
//
// ── The sentinels are not interchangeable either ──────────────────────────
//
// Lite exports every *name* `dispatch` uses (#410), but not the same *values*.
// A lite batch carrying the full SDK's sentinel (10.14.1) fails to commit:
//
//     serverTimestamp() → invalid-argument "Unsupported field value: a custom
//                         ServerTimestampFieldValueImpl object"
//     deleteField()     → … "a custom DeleteFieldValueImpl object"
//     arrayUnion('a')   → … "a custom ArrayUnionFieldValueImpl object"
//     Timestamp         → … "a custom Timestamp object"  (`Timestamp` is a
//                         different class in each entry point — not even
//                         `instanceof` the other)
//
// Each is a **throw inside `dispatch`**, which the queue reads as transient
// (#277: a retry budget spent on a bug in our own code). So
// `liteSentinelFactories` below is built from lite's own exports, and
// `test/domain/liteWritePath.test.js` holds the two tables to the same keys.
//
// The *encode* side is untouched: `opCodec.js` recognises a caller's sentinel
// by the full SDK's `FieldValue` class, and every call site still imports from
// `firebase/firestore`. Only the rebuild at dispatch time moves.

import { getApp, getApps } from 'firebase/app';
import { OUTCOME } from './queue';
import { KIND, TRANSFORM, decodeFields } from './opCodec';

// ── `firebase/firestore/lite` is loaded on demand (#410) ──────────────────
//
// A static import here would fail **every `ui` suite that reaches
// `src/data/writes.js` at load**, with one of two errors from the two builds
// jest can resolve:
//
//   * **jsdom + `require`.** `@firebase/firestore@10.14.1` maps
//     `"./lite" → browser → require → ./dist/lite/index.cjs.js`, and that file
//     is **ESM** — its first line is `import { _registerComponent, … } from
//     '@firebase/app'`. *"Cannot use import statement outside a module"*. The
//     full SDK's `dist/index.cjs.js` beside it is real CommonJS, so this is a
//     packaging mistake upstream (#414). `scripts/check-lite-packaging.mjs`
//     reads that file on every `check:static` and fails when it stops being
//     ESM; it cannot see the second bullet below.
//   * **the `node` build in jsdom.** Point a mapper at
//     `dist/lite/index.node.cjs.js` instead and it `require`s `undici`, which
//     reads `ReadableStream` at module scope. jsdom has none:
//     *"ReferenceError: ReadableStream is not defined"*.
//
// So no build of lite loads in jsdom at import time. Loaded inside the
// dispatch, **a session that never takes the lite branch never loads lite** —
// every session while `liteWriteFlag.js` is off.
//
// `classifyCommitError` below is outside this: it is arithmetic
// over an error's `code` and needs no SDK, which is what lets
// `test/domain/liteWritePath.test.js` drive it against errors from a real
// server and lets `test/ui/liteDispatchRouting.test.jsx` drive it at all.
let liteModule = null;
async function loadLite() {
  if (!liteModule) liteModule = await import('firebase/firestore/lite');
  return liteModule;
}

/**
 * Lite's own sentinel constructors, keyed exactly as `writes.js`'s are.
 *
 * `enqueuedAt` is the only one handed a stored value rather than rebuilding a
 * sentinel from nothing — every dispatch of the row must produce the *same*
 * instant, or `createdAtUnchanged()` in `firestore.rules` refuses the
 * redelivery (#232). Lite's `Timestamp` is a different class from the full
 * SDK's, so this table's version is the one a lite batch will take.
 *
 * A function of the module rather than a constant, because the module arrives
 * asynchronously — see above.
 */
export function liteSentinelFactories(m) {
  return {
    [TRANSFORM.serverTimestamp]: m.serverTimestamp,
    [TRANSFORM.deleteField]: m.deleteField,
    [TRANSFORM.arrayUnion]: m.arrayUnion,
    [TRANSFORM.arrayRemove]: m.arrayRemove,
    [TRANSFORM.enqueuedAt]: (ms) => m.Timestamp.fromMillis(ms),
  };
}

// The lite `Firestore`, built on first use and kept.
//
// **`getApp()` rather than a handle passed in.** A caller's
// `DocumentReference` is the full SDK's and lite refuses it; importing
// `src/lib/firebase.js` is the #112 `import.meta` parse error in every test
// project that touches a write; and `startWriteQueue(db)` is not called by
// most integration suites. `firebase/app` alone has no `import.meta` and works
// in both.
let lite = null;
/** Said once per session — see `dispatchViaLite`. */
let warnedAboutApp = false;
/** `[host, port]` an emulator was declared at, applied when the handle is built. */
let emulator = null;

/**
 * The lite `Firestore` for the default app, or `null` if there is not one yet.
 *
 * `null` rather than a throw: the caller parks the batch as `unreachable`,
 * spends no retry, destroys nothing, and sends it unchanged once a handle
 * exists. A throw would be read by the queue as transient (#277).
 */
export async function liteFirestore() {
  if (lite) return lite;
  if (!getApps().length) return null;
  const m = await loadLite();
  lite = m.getFirestore(getApp());
  // Before anything has used it, which is what `connectFirestoreEmulator`
  // requires and what building it here guarantees.
  if (emulator) m.connectFirestoreEmulator(lite, emulator[0], emulator[1]);
  return lite;
}

/**
 * Point the lite write path at an emulator.
 *
 * **Called unconditionally wherever the full SDK's emulator is wired, not
 * behind the flag** — `src/lib/firebase.js` and
 * `test/integration/firebaseTestApp.js`. The flag is a `localStorage` value
 * flipped in a console; if the wiring were conditional on it, flipping it on
 * in a dev browser would send writes at production, silently.
 *
 * **It records rather than connects**, because the handle does not exist yet —
 * lite is not loaded until a dispatch needs it. `connectFirestoreEmulator`
 * must be called before the handle is used, and applying it at construction
 * makes that structural.
 */
export function connectLiteEmulator(host, port) {
  emulator = [host, port];
}

/** Tests only: forget the memoised handle, so a fresh app can be attached. */
export function __resetLiteFirestore() {
  lite = null;
  warnedAboutApp = false;
}

/**
 * What a rejected lite `commit()` means for the queue.
 *
 * ── Why lite needs one ─────────────────────────────────────────────────────
 *
 * The full SDK holds an unsendable write in its own durable mutation queue and
 * leaves the promise pending, which is why `writes.js` can treat every
 * rejection as permanent. Lite's `commit()` is a single REST call: it
 * **rejects promptly** when the server cannot be reached, so with no
 * classifier every write made offline would be dead-lettered as refused.
 *
 * **The offline code is not `'unavailable'`.** The codes lite gives
 * (10.14.1), asserted against a real HTTP server by
 * `test/domain/liteWritePath.test.js`:
 *
 * | what happened                    | `err.code`           |
 * |----------------------------------|----------------------|
 * | connection refused (no network)  | `unknown`            |
 * | server hung up mid-request       | `unknown`            |
 * | 400 Bad Request                  | `failed-precondition`|
 * | 401 Unauthorized                 | `unauthenticated`    |
 * | 403 Forbidden                    | `permission-denied`  |
 * | 404 Not Found                    | `not-found`          |
 * | 409 Conflict                     | `aborted`            |
 * | 429 Too Many Requests            | `resource-exhausted` |
 * | 499 Client Closed Request        | `cancelled`          |
 * | 500 Internal Server Error        | `unknown`            |
 * | 501 Not Implemented              | `unimplemented`      |
 * | 502 / 507 / other 5xx            | `internal`           |
 * | 503 Service Unavailable          | `unavailable`        |
 * | 504 Gateway Timeout              | `deadline-exceeded`  |
 *
 * `fetch` throwing — no network, DNS gone — is caught in `FetchConnection` and
 * rethrown through `mapCodeFromHttpStatus(e.status)` on a `TypeError` that has
 * no `status`; that logs *"HTTP error has no status"* and returns `unknown`.
 * HTTP 500 returns `unknown` from the same table.
 *
 * **So `unknown` is the offline code, and a permanent 500 arrives under it
 * too.** It maps to [OUTCOME.unreachable], so a server broken for good is
 * retried for as long as it lasts, spending no retry budget: a write parked
 * for ever is recoverable and a dead-lettered one is not. A queue that will
 * not drain points here.
 *
 * ── The three groups ──────────────────────────────────────────────────────
 *
 *   * **`unreachable`** — nobody judged this write. Either we never reached a
 *     server (`unknown`, and its 500 passenger) or we reached something in
 *     front of one that had no answer (`unavailable`, `deadline-exceeded`,
 *     `internal`, `cancelled`). No retry budget is spent, nothing is
 *     destroyed, and it goes out unchanged when the network returns —
 *     **by way of `queue.js`'s `nudge()`, not by waiting**: the park for
 *     `unreachable` escalates to `maxBackoffMs`, five minutes, and
 *     `writes.js`'s `online` listener releases it on reconnect.
 *   * **`transient`** — a server answered, and the answer was *not now*:
 *     `resource-exhausted` (429) and `aborted` (409). Retrying is right and
 *     **bounded** — the budget turns an outage that never ends into a dead
 *     letter a teacher can see (#162, #125).
 *   * **`permanent`** — the server judged the write and said no.
 *     `permission-denied`, `unauthenticated`, `failed-precondition`,
 *     `not-found`, `out-of-range`, `unimplemented`, `invalid-argument`. The
 *     default: an unrecognised code is likelier to be a refusal than a network
 *     event.
 */
export function classifyCommitError(error) {
  switch (error?.code) {
    // Nobody judged this write.
    case 'unknown':
    case 'unavailable':
    case 'deadline-exceeded':
    case 'internal':
    case 'cancelled':
      return OUTCOME.unreachable;
    // A server answered "not now".
    case 'resource-exhausted':
    case 'aborted':
      return OUTCOME.transient;
    default:
      return OUTCOME.permanent;
  }
}

/**
 * Hand one queued batch to `firebase/firestore/lite`.
 *
 * Returns the queue's own answer shape — `{ outcome, error? }` — and **never
 * throws**: a throw would be read as transient by the queue and spend a real
 * write's retry budget on a bug in this file (#277).
 *
 * The two things that can fail before a `commit()` are each caught, with a
 * console line saying which. Loading lite is `unreachable`: it spends and
 * destroys nothing, and the reconnect that releases the park is what lets an
 * offline browser fetch the chunk. Building the batch is `transient` (#423):
 * the same record fails the same way every time — see the catch.
 */
export async function dispatchViaLite(record) {
  let db;
  try {
    db = await liteFirestore();
  } catch (error) {
    // Lite is a dynamic import, so the first lite dispatch a browser makes may
    // have to *fetch* it, which an offline browser cannot. Nobody judged this
    // write and no retry is spent; the batch goes out unchanged once the chunk
    // can be loaded, on the `online` event that releases the park (`nudge()` in
    // `queue.js`).
    //
    // A bug in `liteFirestore` would land here too, indistinguishably, so the
    // message gives both readings.
    console.error(
      '[writes] could not obtain a `firebase/firestore/lite` handle, so batch '
      + `${record.batchId} is being parked as unreachable (#410). Two cases this cannot tell `
      + 'apart: the lite chunk could not be fetched, which is what being offline looks like '
      + 'for the first lite write a browser makes; and a defect in liteFirestore() itself. '
      + 'Either way the write is durable, unsent, not refused by anybody, and no retry has '
      + 'been spent.',
      error,
    );
    return { outcome: OUTCOME.unreachable };
  }
  if (!db) {
    // Said once, as `writes.js` says its twin: this polls on the queue's base
    // backoff while it lasts.
    if (!warnedAboutApp) {
      warnedAboutApp = true;
      console.error(
        '[writes] no `FirebaseApp` has been initialised, so a queued batch cannot be dispatched '
        + 'over `firebase/firestore/lite` (#410). The write is durable and unsent — it is **not** '
        + 'refused by the server, and no retry has been spent. Nothing fires when an app is '
        + 'created, so it goes out on the next thing that drains the queue: a sign-in, a '
        + 'reconnect, another save, or the escalating park expiring.',
      );
    }
    return { outcome: OUTCOME.unreachable };
  }

  let batch;
  try {
    const m = await loadLite();
    const sentinels = liteSentinelFactories(m);
    batch = m.writeBatch(db);
    for (const op of record.ops) {
      const ref = m.doc(db, op.path);
      if (op.kind === KIND.delete) {
        batch.delete(ref);
        continue;
      }
      const data = decodeFields(
        record.fields.filter((f) => f.opId === op.id), sentinels,
      );
      if (op.kind === KIND.merge) batch.set(ref, data, { merge: true });
      else batch.set(ref, data);
    }
  } catch (error) {
    // **Three readings this catch cannot tell apart**, so it gives all three:
    //
    //   * `m.doc(db, op.path)` throws on a malformed path — an odd segment
    //     count, an empty segment — which comes from whatever **call site**
    //     enqueued the op, not from here.
    //   * `decodeFields` throws on a stored record carrying a transform no
    //     factory in `liteSentinelFactories` covers — a record written by a
    //     different build of this app.
    //   * and the sentinel table, the batch construction and the `KIND`
    //     dispatch really are this file's.
    //
    // ── Transient, not `unreachable` (#423) ────────────────────────────────
    //
    // All three are a fact about the *record*, so building it again gives the
    // same throw, and nothing that releases a park can change the answer.
    // Under the studio-order rule the queue takes for lite
    // (`readyBatchesInStudioOrder`, `core/src/queue.rs`), a batch parked for
    // ever would hold every later write of the studio behind it. `transient`
    // spends the retry budget instead and ends in a dead letter a teacher can
    // see, which blocks nothing — the same as the full-SDK branch in
    // `writes.js`, since the queue reads any throw out of `dispatch` as
    // transient.
    console.error(
      `[writes] building batch ${record.batchId} for \`firebase/firestore/lite\` failed (#410). `
      + 'This is a defect somewhere in this app rather than an answer from a server, and this '
      + 'catch cannot tell which of three: a malformed `op.path` from the call site that '
      + 'enqueued it; a stored op carrying a transform this build has no factory for, which is a '
      + 'record written by an older build; or a bug in src/data/liteDispatch.js itself. It is '
      + 'being answered as transient (#423): the same record will fail the same way, so the '
      + 'retry budget is what ends it, in a dead letter, rather than a park that would hold '
      + 'every later write to this studio behind it.',
      error,
    );
    return { outcome: OUTCOME.transient, error };
  }

  try {
    await batch.commit();
    return { outcome: OUTCOME.acknowledged };
  } catch (error) {
    return { outcome: classifyCommitError(error), error };
  }
}
